import * as THREE from 'three';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { drawTextFromCode } from '../../commands/drawTextFromCode';
function drawTextForMachine(text, position, rotationRad = 0, fontSize = 18) {
  const dummyScene = new THREE.Scene();
  const group = drawTextFromCode(dummyScene, {
    text,
    position,
    fontSize,
    color: "#ffffff",
    alignment: 'center',
    halign: 1,
    valign: 0,
    rotation: (rotationRad * 180) / Math.PI,
  });

  if (!group || !(group instanceof THREE.Object3D)) {
    console.warn("drawTextForMachine failed to return valid Object3D.");
    return new THREE.Group();
  }
  return group;
}

export function createMachineSymbol(
  scene = null,
  mousePos,
  dir = 1,
  color,
  isPreview = false,
  type = "Bulaşık Makinesi",
  power = "0",
  angle,
  providedId = null
) {
  const boxSize = 50;
  const half = boxSize / 2; // 25
  const rotationGizmoRadius = 6;
  const rotationGizmoColor = 0xff0000;

  const colorResult = (typeof color !== 'undefined' && color !== null)
    ? color
    : (isPreview ? 0xcc5600 : 0x000000);

  const group = new THREE.Group();
  const offsetGroup = new THREE.Group();
  offsetGroup.userData = { isOffsetGroup: true };

  const machineId = providedId || generateUniqueId('machine');
  group.position.set(mousePos.x, mousePos.y, 0);

  group.userData = {
    id: machineId,
    type: 'machine',
    isSelectable: true,
    isPreview,
    power,
    stretchLength: 0,
    machineType: type,
    basePoint: mousePos,
    angle,
    dir,
    originalColor: colorResult,
  };

  group.add(offsetGroup);

  // ✅ Offset hesaplama (sembolü wire ucundan öteye taşı — yakın kenar wire ucuna değsin).
  // Makine kutusu 50 geniş (yarısı 25). İnterkom 15 geniş (yarısı 7.5) → offset 7.5.
  const offsetDist = (type === "İnterkom") ? 7.5 : 25;
  if (dir instanceof THREE.Vector3) {
    offsetGroup.position.set(dir.x * offsetDist, dir.y * offsetDist, 0);
  } else {
    // Eğer dir sayısal ise (flipDirection), x ekseninde offset uygula (varsayılan)
    // Ancak genelde dir Vector3 olarak gelir.
    // Sayısal dir durumunda rotasyon offsetGroup.rotation.z ile hallediliyor,
    // bu yüzden offset'i lokal x ekseninde verebiliriz.
    offsetGroup.position.set(offsetDist, 0, 0);
  }

  // -------------------------
  // 1) KARE (ORTALANMIŞ)
  // -------------------------
  // İnterkom kendi 15×25 panelini çizer — ortak 50×50 kareyi atla.
  if (type !== "İnterkom") {
    const squarePoints = [
      new THREE.Vector3(-half, -half, 0),
      new THREE.Vector3(-half, half, 0),
      new THREE.Vector3(half, half, 0),
      new THREE.Vector3(half, -half, 0)
    ];

    const square = new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints(squarePoints),
      new THREE.LineBasicMaterial({ color: colorResult })
    );
    offsetGroup.add(square);
  }

  // -------------------------
  // 2) TÜM SEMBOL MERKEZDEN ÇİZİLİR
  // -------------------------

  // YARDIMCI FONKSİYONLAR
  const makeCircle = (r, c) =>
    new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints(
        [...Array(64)].map((_, i) => {
          const t = (i / 64) * Math.PI * 2;
          return new THREE.Vector3(Math.cos(t) * r, Math.sin(t) * r, 0);
        })
      ),
      new THREE.LineBasicMaterial({ color: c })
    );

  const makeFilledCircle = (r, c) =>
    new THREE.Mesh(
      new THREE.CircleGeometry(r, 32),
      new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide })
    );

  // -------------------
  // TİP BAZLI SEMBOLLER
  // -------------------

  if (type === "Çamaşır Makinası") {
    const c1 = makeCircle(20, colorResult);
    const c2 = makeCircle(9, colorResult);
    c1.position.set(0, 0, 0.1);
    c2.position.set(0, 0, 0.2);
    offsetGroup.add(c1);
    offsetGroup.add(c2);
  }

  // ------------- KURUTMA MAKİNESİ (çamaşır kopyası: dış daire 20 + ortalı "K.M.")
  else if (type === "Kurutma Makinesi") {
    const baseAngleRad = Math.atan2(dir.y, dir.x);
    const c1 = makeCircle(20, colorResult);
    c1.position.set(0, 0, 0.1);
    offsetGroup.add(c1);
    const label = drawTextForMachine("K.M.", { x: 0, y: 0 }, baseAngleRad, 12);
    offsetGroup.add(label);
  }

  else if (type === "Bulaşık Makinesi") {
    const c1 = makeCircle(20, colorResult);
    c1.position.set(0, 0, 0.1);
    offsetGroup.add(c1);

    const c2 = makeCircle(10, colorResult);
    c2.position.set(0, 0, 0.1);
    offsetGroup.add(c2);

    const line = (x1, y1, x2, y2) =>
      new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(x1, y1, 0),
          new THREE.Vector3(x2, y2, 0)
        ]),
        new THREE.LineBasicMaterial({ color: colorResult })
      );

    offsetGroup.add(line(-14.1421, -14.1421, -25, -25));
    offsetGroup.add(line(-14.1421, 14.1421, -25, 25));
    offsetGroup.add(line(14.1421, 14.1421, 25, 25));
    offsetGroup.add(line(14.1421, -14.1421, 25, -25));
  }

  else if (type === "Fırın") {
    const dot = makeFilledCircle(5, colorResult);
    dot.position.set(0, 0, 0.1);
    offsetGroup.add(dot);

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-25, 15, 0),
        new THREE.Vector3(25, 15, 0)
      ]),
      new THREE.LineBasicMaterial({ color: colorResult })
    );
    offsetGroup.add(line);
  }

  else if (type === "Klima") {
    const dot = makeFilledCircle(5, colorResult);
    dot.position.set(0, 10, 0.1);
    offsetGroup.add(dot);

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-25, 0, 0),
        new THREE.Vector3(25, 0, 0)
      ]),
      new THREE.LineBasicMaterial({ color: colorResult })
    );
    offsetGroup.add(line);
  }

  // ------------- MOTOR (çamaşır makinesi kopyası: kare + dış daire 20 + ortalı "M")
  // İç küçük daire YOK; kalan dairenin içine ortalı "M" harfi. Sembolün altında güç.
  else if (type === "Motor") {
    const baseAngleRad = Math.atan2(dir.y, dir.x);

    const c1 = makeCircle(20, colorResult);
    c1.position.set(0, 0, 0.1);
    offsetGroup.add(c1);

    const label = drawTextForMachine("M", { x: 0, y: 0 }, baseAngleRad, 24);
    offsetGroup.add(label);

    if (power && power !== "0") {
      const powerLabel = drawTextForMachine(`${power}W`, { x: 0, y: -35 }, baseAngleRad, 12);
      offsetGroup.add(powerLabel);
    }
  }

  // ------------- FAN & etc. (pivot merkezli)
  else if (type === "Fan") {
    const c1 = makeCircle(12, colorResult);
    c1.position.set(0, 0);
    offsetGroup.add(c1);

    const baseAngleRad = Math.atan2(dir.y, dir.x);
    const label = drawTextForMachine("F", { x: 0, y: 0 }, baseAngleRad);
    offsetGroup.add(label);

    if (power && power !== "0") {
      const powerLabel = drawTextForMachine(`${power}W`, { x: 0, y: -20 }, baseAngleRad, 12);
      offsetGroup.add(powerLabel);
    }
  }

  // ------------- İNTERKOM (15×25 panel: dış dörtgen + daire + ekran + 3 buton)
  else if (type === "İnterkom") {
    const rect = (x1, y1, x2, y2) => new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(x1, y1, 0.1),
        new THREE.Vector3(x1, y2, 0.1),
        new THREE.Vector3(x2, y2, 0.1),
        new THREE.Vector3(x2, y1, 0.1),
      ]),
      new THREE.LineBasicMaterial({ color: colorResult })
    );

    // Dış panel: alt kenar 15 (x: -7.5..7.5), dikey yan 25 (y: -12.5..12.5)
    offsetGroup.add(rect(-7.5, -12.5, 7.5, 12.5));
    // Üstte daire (kamera/zil)
    const c = makeCircle(2.6, colorResult);
    c.position.set(0, 7.5, 0.1);
    offsetGroup.add(c);
    // Ortada ekran
    offsetGroup.add(rect(-4.5, -4.5, 4.5, 3.5));
    // Altta 3 buton
    [-4, 0, 4].forEach(cx => offsetGroup.add(rect(cx - 1.6, -10.5, cx + 1.6, -8.5)));
  }

  // -------------------------
  // ROTATE GİZMO – MERKEZLİ
  // -------------------------
  const rotateGizmo = makeFilledCircle(rotationGizmoRadius, rotationGizmoColor);
  rotateGizmo.userData = { type: "rotateGizmo", parentMachineId: machineId };
  rotateGizmo.position.set(0, half + 10, 1);
  rotateGizmo.visible = false;
  offsetGroup.add(rotateGizmo);

  // -------------------------
  // BAŞLANGIÇ ROTASYONU
  // -------------------------
  if (typeof angle === "number") {
    offsetGroup.rotation.z = angle;
  }

  return group;
}
